import type { AssignmentGroupingType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import type { Control, UseFormSetValue, UseFormTrigger } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { BreakoutDateTimeInput } from 'components/design-system/BreakoutDateTimeInput'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useTranslationTyped } from 'i18n/i18n'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { FormError } from 'components/design-system/form/FormError'
import type { AssignmentFormValues } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { DateTime } from 'luxon'
import type { MutableRefObject } from 'react'
import { useMemo } from 'react'
import { getDefaultLateSubmissionCutoff, getDueDateBounds } from '../helpers'
import { useCutoffIsAutoDerived } from '../useCutoffIsAutoDerived'
import { BreakoutSwitch } from 'components/design-system/BreakoutSwitch'
import { observer } from 'mobx-react-lite'

export const DateInputs = ({
  control,
  assignedAtInitialView,
  dueDateInitialView,
  isSubmitting,
  hasBeenStarted,
  isDraft,
  initialAssignedAt,
  initialDueDate,
  assignedAt,
  dueDate,
  formValid,
  readOnly = false,
  allowLateSubmissions,
  sectionEndDate,
  sectionStartDate,
  releasedToStudents,
  groupingType,
  setValue,
  trigger,
}: {
  control: Control<AssignmentFormValues>
  dueDateInitialView: DateTime | undefined
  assignedAtInitialView: DateTime | undefined
  isSubmitting: boolean
  hasBeenStarted: boolean
  isDraft: boolean
  initialAssignedAt: DateTime | undefined
  initialDueDate: DateTime | undefined
  assignedAt: DateTime
  releasedToStudents: boolean
  groupingType: AssignmentGroupingType
  dueDate: DateTime
  formValid: boolean
  readOnly?: boolean
  allowLateSubmissions: boolean | undefined
  sectionEndDate?: DateTime
  sectionStartDate?: DateTime
  setValue: UseFormSetValue<AssignmentFormValues>
  trigger: UseFormTrigger<AssignmentFormValues>
}) => {
  const cutoffIsAutoDerived = useCutoffIsAutoDerived(control)

  return (
    <>
      <MainDateInputs
        control={control}
        assignedAtInitialView={assignedAtInitialView}
        dueDateInitialView={dueDateInitialView}
        setValue={setValue}
        isSubmitting={isSubmitting}
        hasBeenStarted={hasBeenStarted}
        isDraft={isDraft}
        initialAssignedAt={initialAssignedAt}
        initialDueDate={initialDueDate}
        assignedAt={assignedAt}
        dueDate={dueDate}
        formValid={formValid}
        readOnly={readOnly}
        allowLateSubmissions={allowLateSubmissions}
        sectionEndDate={sectionEndDate}
        sectionStartDate={sectionStartDate}
        releasedToStudents={releasedToStudents}
        groupingType={groupingType}
        trigger={trigger}
        cutoffIsAutoDerived={cutoffIsAutoDerived}
      />
      <LateSubmissionInputs
        control={control}
        setValue={setValue}
        dueDate={dueDate}
        allowLateSubmissions={allowLateSubmissions}
        readOnly={readOnly}
        sectionEndDate={sectionEndDate}
        cutoffIsAutoDerived={cutoffIsAutoDerived}
      />
    </>
  )
}

const MainDateInputs = ({
  control,
  assignedAtInitialView,
  dueDateInitialView,
  setValue,
  isSubmitting,
  hasBeenStarted,
  isDraft,
  initialAssignedAt,
  initialDueDate,
  assignedAt,
  dueDate,
  formValid,
  trigger,
  readOnly = false,
  allowLateSubmissions,
  sectionEndDate,
  sectionStartDate,
  cutoffIsAutoDerived,
}: {
  control: Control<AssignmentFormValues>
  dueDateInitialView: DateTime | undefined
  assignedAtInitialView: DateTime | undefined
  setValue: UseFormSetValue<AssignmentFormValues>
  isSubmitting: boolean
  hasBeenStarted: boolean
  isDraft: boolean
  initialAssignedAt: DateTime | undefined
  initialDueDate: DateTime | undefined
  assignedAt: DateTime
  releasedToStudents: boolean
  groupingType: AssignmentGroupingType
  dueDate: DateTime
  formValid: boolean
  trigger: UseFormTrigger<AssignmentFormValues>
  readOnly?: boolean
  allowLateSubmissions: boolean | undefined
  sectionEndDate?: DateTime
  sectionStartDate?: DateTime
  cutoffIsAutoDerived: MutableRefObject<boolean>
}) => {
  const { tt } = useTranslationTyped()

  // const dueDateIsDirty = control.getFieldState('dueDate').isDirty
  // const assignedAtIsDirty = control.getFieldState('assignedAt').isDirty
  const dueDateBounds = getDueDateBounds({ assignedAt })

  const showIsAvailableLessThan24Hours = useMemo(() => {
    // if no expires at no comparison
    if (!dueDate || !assignedAt) return false

    // Note (mbunsch): this can also be done like this with RHF:
    // if (!isDraft && !dueDateIsDirty && !assignedAtIsDirty) return
    // if dates didn't change, don't show the warning
    if (
      assignedAt === initialAssignedAt &&
      dueDate === initialDueDate &&
      !isDraft
    )
      return

    // A due date that has already passed leaves no window to warn about.
    // Evaluated when the dates change, not on a clock: a dialog left open
    // while the deadline passes keeps whatever it showed.
    if (dueDate < DateTime.now()) return false

    // don't compare against now unless assigned at is 5 minutes or more in past
    const nowMinus5Minutes = DateTime.now().minus({ minutes: 5 })

    const availableFrom =
      assignedAt < nowMinus5Minutes ? DateTime.now() : assignedAt

    return dueDate.diff(availableFrom, 'hours').hours < 24
  }, [assignedAt, dueDate, initialAssignedAt, initialDueDate, isDraft])

  return (
    <div className={classNames('flex flex-col gap-1')}>
      <BreakoutTooltip
        enabled={hasBeenStarted}
        content={tt.instructor_assignment.disabled_because_its_started()}
      >
        <div>
          <Controller
            control={control}
            name="assignedAt"
            render={({ field, fieldState }) => (
              <BreakoutDateTimeInput
                {...field}
                disabled={isSubmitting || hasBeenStarted || readOnly}
                error={fieldState.error}
                type="datetime-local"
                min={sectionStartDate}
                initialView={assignedAtInitialView}
                label={tt.instructor_assignment.available_from()}
                required={!isDraft}
                kind="secondary"
                id="assignment-start-time"
                onChange={(newAssignedAt) => {
                  field.onChange(newAssignedAt)
                }}
                resolution="15min"
                roundUpToQuarterHour
              />
            )}
          />
        </div>
      </BreakoutTooltip>

      <div>
        <Controller
          control={control}
          name="dueDate"
          render={({ field, fieldState }) => {
            return (
              <>
                <div className="flex w-full flex-row items-center gap-2">
                  <div className="grow">
                    <BreakoutDateTimeInput
                      {...field}
                      type="datetime-local"
                      disabled={readOnly}
                      label={tt.instructor_assignment.due_date()}
                      required={!isDraft}
                      kind="secondary"
                      id="meeting-time"
                      hideNowButton
                      min={dueDateBounds.min}
                      max={dueDateBounds.max}
                      initialView={
                        dueDateInitialView ??
                        assignedAt
                          ?.plus({ week: 1 })
                          .set({ hour: 13, minute: 0 }) ??
                        DateTime.now()
                          .plus({ week: 1 })
                          .set({ hour: 13, minute: 0 })
                      }
                      value={field.value}
                      onChange={(newDueDate) => {
                        // Only re-derive the cutoff while it is still ours to
                        // derive - never overwrite one the instructor picked.
                        if (
                          allowLateSubmissions &&
                          newDueDate &&
                          cutoffIsAutoDerived.current
                        ) {
                          const newDefaultCutoff =
                            getDefaultLateSubmissionCutoff({
                              dueDate: newDueDate,
                              sectionEndDate,
                            })
                          if (newDefaultCutoff) {
                            setValue('lateSubmissionCutoff', newDefaultCutoff, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }
                        }

                        field.onChange(newDueDate)
                        trigger([
                          'assignedAt',
                          'dueDate',
                          'lateSubmissionCutoff',
                        ]) // Validate all the date fields
                      }}
                      resolution="15min"
                      roundUpToQuarterHour
                    />
                  </div>
                </div>
                {<FormError error={fieldState.error} />}
                {formValid && showIsAvailableLessThan24Hours && (
                  <div className="mt-2 flex w-full flex-row items-center gap-2 rounded-xl bg-core-error px-3 py-2 text-body-medium text-core-on-error">
                    <TriangleAlertIcon size={24} />
                    <span>
                      {tt.instructor_assignment.configure_assignment_dialog.less_than_24_hours_warning()}
                    </span>
                  </div>
                )}
              </>
            )
          }}
        />
      </div>
    </div>
  )
}

export const LateSubmissionInputs = observer(function LateSubmissionInputs({
  control,
  setValue,
  dueDate,
  allowLateSubmissions,
  readOnly,
  sectionEndDate,
  cutoffIsAutoDerived,
}: {
  control: Control<AssignmentFormValues>
  setValue: UseFormSetValue<AssignmentFormValues>
  dueDate: DateTime
  allowLateSubmissions: boolean | undefined
  readOnly: boolean
  sectionEndDate?: DateTime
  cutoffIsAutoDerived: MutableRefObject<boolean>
}) {
  const { tt } = useTranslationTyped()

  return (
    <>
      <div className="mt-2">
        <Controller
          control={control}
          name="allowLateSubmissions"
          render={({ field }) => (
            <BreakoutSwitch
              testId="allow-late-submissions"
              label={tt.instructor_assignment.configure_assignment_dialog.allow_late_submissions()}
              labelPosition="left"
              className="mt-1 w-full justify-between gap-4 pr-3"
              checked={field.value || false}
              onChange={(checked) => {
                const isChecked = checked.target.checked

                // Update both fields
                // When checking: don't validate immediately to avoid showing errors for autofilled dates
                // When unchecking: validate to clear errors and ensure form is valid
                setValue('allowLateSubmissions', isChecked, {
                  shouldValidate: false,
                })

                const defaultCutoffDate = isChecked
                  ? getDefaultLateSubmissionCutoff({
                      dueDate,
                      sectionEndDate,
                    })
                  : undefined

                // The switch replaces whatever cutoff was there with a derived
                // one (or clears it), so the field goes back to being ours.
                cutoffIsAutoDerived.current = true

                setValue('lateSubmissionCutoff', defaultCutoffDate, {
                  shouldValidate: !isChecked, // Validation is skipped when turning the switch on, to avoid showing an error for the default value before the user gets a chance to adjust it
                  shouldDirty: true,
                })
              }}
              disabled={readOnly}
              accent
            />
          )}
        />
      </div>

      {allowLateSubmissions && (
        <div className="mt-3">
          <Controller
            control={control}
            name="lateSubmissionCutoff"
            render={({ field, fieldState }) => (
              <BreakoutDateTimeInput
                {...field}
                type="datetime-local"
                disabled={readOnly}
                label={tt.instructor_assignment.configure_assignment_dialog.late_submission_cutoff()}
                required={true}
                kind="secondary"
                id="late-submission-cutoff"
                hideNowButton
                min={dueDate ? dueDate.plus({ minutes: 15 }) : DateTime.now()}
                max={dueDate?.plus({ days: 365 })}
                initialView={
                  dueDate?.plus({ weeks: 1 }) ??
                  DateTime.now().plus({ weeks: 1 })
                }
                value={field.value}
                onChange={(newCutoff) => {
                  // The instructor picked this value, so the due date must stop
                  // deriving it.
                  cutoffIsAutoDerived.current = false
                  field.onChange(newCutoff)
                }}
                error={fieldState.error}
                resolution="15min"
                roundUpToQuarterHour
              />
            )}
          />
        </div>
      )}
    </>
  )
})
